import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser } from "@/lib/sentry";
import { Session, User } from '@supabase/supabase-js';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export type UserType = 'customer' | 'supplier' | null;

export interface SupplierProfile {
  id: string;
  user_id: string;
  company_name: string;
  first_name: string;
  last_name: string;
  company_email: string;
  phone_number?: string;
  address: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  website?: string;
  description?: string;
  sub_category?: string;
  org_number?: string;
  category?: string;
  title?: string;
  personal_email?: string;
  terms_for_advertisers?: boolean;
  terms_for_users?: boolean;
  Activated?: boolean;
  cost_percentage?: number | null;
  cost_fixed_fee?: number | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userType: UserType;
  supplierProfile: SupplierProfile | null;
  isAnonymous: boolean;
  isClaimed: boolean;
  signInWithEmail: (email: string, password: string, loginType?: 'customer' | 'supplier', rememberMe?: boolean) => Promise<{ error: any }>;
  signInWithProvider: (provider: 'google' | 'facebook', rememberMe?: boolean) => Promise<{ error: any }>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  signInAnonymously: () => Promise<{ data?: any; error: any }>;
  checkEmailExists: (email: string) => Promise<{ exists: boolean; profile: any }>;
  convertAnonymousAccount: (email: string, password: string) => Promise<{ error: any }>;
  refreshSession: () => Promise<boolean>;
  resetPasswordForEmail: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<UserType>(null);
  const [supplierProfile, setSupplierProfile] = useState<SupplierProfile | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isClaimed, setIsClaimed] = useState(false);
  const { toast } = useToast();

  const handleErrorAndToast = (error: any, defaultTitle: string, defaultDescription: string) => {
    console.error(`Error (${defaultTitle}):`, error);
    const description = error?.message || defaultDescription;
    toast({
      title: defaultTitle,
      description,
      variant: "destructive",
    });
    return { error };
  };

  const clearAuthState = () => {
    console.log('Clearing auth state');
    setUser(null);
    setSession(null);
    setUserType(null);
    setSupplierProfile(null);
    setIsAnonymous(false);
    setIsClaimed(false);
    setLoading(false);
  };

  // Helper to determine user type and fetch profile
  const getUserTypeAndProfile = async (userId: string): Promise<{ type: UserType; profile: SupplierProfile | null; error?: any }> => {
    try {
      console.log('Checking user type for userId:', userId);

      // Check for supplier profile first
      const { data: supplierData, error: supplierError } = await supabase
        .from('supplier_profile')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (supplierError) {
        console.error('Error checking supplier profile:', supplierError);
        return { type: null, profile: null, error: supplierError };
      }

      if (supplierData) {
        console.log('User found in supplier_profile table - supplier');
        const profile = { ...supplierData, postal_code: supplierData.postal_code?.toString() || '' };
        setSentryUser({
          id: userId,
          role: "supplier"
        });
        return { type: 'supplier', profile };
      }

      // Check for customer profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, is_anonymous, is_claimed')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error checking customer profile:', profileError);
        return { type: null, profile: null, error: profileError };
      }

      if (profileData) {
        console.log('User found in profiles table - customer');
        setIsAnonymous(profileData.is_anonymous || false);
        setIsClaimed(profileData.is_claimed || false);
        setSentryUser({
          id: userId,
          role: "customer"
        });
        return { type: 'customer', profile: null };
      }

      console.log('User exists in auth but not in database tables - marking as null');
      return { type: null, profile: null };

    } catch (error) {
      console.error('Error determining user type:', error);
      return { type: null, profile: null, error };
    }
  };

  const checkEmailExists = async (email: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return { exists: !!data, profile: data };
    } catch (error: any) {
      console.error('Error checking email:', error);
      return { exists: false, profile: null };
    }
  };

  const updateAuthState = async (newSession: Session | null) => {
    if (newSession) {
      setUser(newSession.user);
      setSession(newSession);
      const { type, profile, error } = await getUserTypeAndProfile(newSession.user.id);

      // If there was an error with the database lookup, don't trigger state updates that might clear things
      // This prevents users from being "logged out" in the UI during temporary network issues
      if (error) {
        console.log('Error determining user type, maintaining current session for stability');
        setLoading(false);
        return;
      }

      setUserType(type);
      setSupplierProfile(profile);

      // Only force sign out if we're CERTAIN the user has no profile and isn't an admin
      if (!type) {
        console.log('Invalid user detected - triggering force sign out');
        await supabase.auth.signOut();
        handleErrorAndToast(null, "Ogiltigt konto", "Kontot finns inte i databasen.");
      }
    } else {
      clearAuthState();
    }
    setLoading(false);
  };

  // Handle magic link verification from URL on initial load
  useEffect(() => {
    const handleMagicLink = async () => {
      // 1. Check for token_hash and type (can be in hash or search params)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const searchParams = new URLSearchParams(window.location.search);

      const tokenHash = hashParams.get('token_hash') || searchParams.get('token_hash');
      const type = hashParams.get('type') || searchParams.get('type');

      // 2. Only handle 'magiclink' type here. 
      // Other types like 'recovery' or 'email_change' have their own dedicated pages.
      if (tokenHash && type === 'magiclink') {
        console.log('🔗 AuthContext: Detected magiclink token, verifying...');
        setLoading(true);

        try {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'magiclink',
          });

          if (error) {
            console.error('❌ AuthContext: Magic link verification failed:', error.message);
            toast({
              title: "Inloggning misslyckades",
              description: "Länken är ogiltig eller har gått ut.",
              variant: "destructive",
            });
          } else if (data.session) {
            console.log('✅ AuthContext: Magic link verified successfully');
            toast({
              title: "Inloggad!",
              description: "Välkommen tillbaka.",
            });
            // Initial state update with new session
            updateAuthState(data.session);
          }
        } catch (err) {
          console.error('❌ AuthContext: Unexpected error during magic link verification:', err);
        } finally {
          // Clean up the URL to prevent re-verification on reload
          const { origin, pathname, search } = window.location;
          // Clear hash if it contained the auth params
          if (window.location.hash.includes('token_hash')) {
            window.history.replaceState({}, "", origin + pathname + search);
          }
          setLoading(false);
        }
      }
    };

    handleMagicLink();
  }, []);

  // Main Effect Hook
  useEffect(() => {
    console.log('Setting up auth state listener');

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔥 Auth state change event:', event);
        if (event === 'SIGNED_OUT') {
          clearAuthState();
          toast({
            title: "Utloggad",
            description: "Du har loggats ut.",
          });
        } else {
          updateAuthState(session);
        }
      }
    );

    // Initial session check
    const checkInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      updateAuthState(session);
    };

    checkInitialSession();

    return () => {
      console.log('Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Anonymous Sign-In Flow
   * 
   * Creates an authenticated Supabase user WITHOUT email verification.
   * This user receives a valid auth.uid() immediately and can access RLS-protected tables.
   * 
   * Usage:
   * 1. User fills out customer request form (unauthenticated)
   * 2. Call signInAnonymously() to create auth.uid()
   * 3. Create profiles record with is_anonymous=true, is_claimed=false
   * 4. Create customer_requests record with profile_id=auth.uid()
   * 5. Send verification email
   * 6. User verifies → call convertAnonymousAccount()
   * 
   * RLS Behavior:
   * - Anonymous users CAN access customer_requests (via auth.uid() = profile_id)
   * - Anonymous users CAN view conversations (via ownership chain)
   * - Anonymous users CANNOT access supplier features (is_supplier() returns FALSE)
   * - Anonymous users use the "public" Postgres role
   */
  const signInAnonymously = async () => {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) {
      toast({
        title: "Anonym inloggning misslyckades",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
    return { data, error: null };
  };

  /**
   * Convert Anonymous Account to Verified Customer
   * 
   * Upgrades an anonymous user (auth.uid() exists, email unverified) to a claimed customer.
   * 
   * Flow:
   * 1. Verify current user is anonymous (user.is_anonymous === true)
   * 2. Call supabase.auth.updateUser() with new email + password
   * 3. Supabase automatically sends verification email
   * 4. User clicks email link → Supabase marks email as verified
   * 5. System updates profiles: is_anonymous=false, is_claimed=true
   * 
   * Important:
   * - auth.uid() DOES NOT CHANGE - all existing customer_requests remain linked
   * - User retains access to all conversations and data
   * - After verification, user gains full customer permissions (guestlist, budget, checklist)
   * - Only metadata flags change (is_anonymous, is_claimed)
   * 
   * RLS Impact:
   * - Before: User has "public" role, limited permissions
   * - After: User has "authenticated" role, full customer permissions
   * - Policies using auth.uid() continue to work seamlessly
   */
  const convertAnonymousAccount = async (email: string, password: string) => {
    if (!user?.is_anonymous) {
      toast({
        title: "Kontokonvertering misslyckades",
        description: "Nuvarande användare är inte anonym",
        variant: "destructive",
      });
      return { error: new Error("Not an anonymous user") };
    }

    // Update email and password - Supabase will send verification email
    const { error } = await supabase.auth.updateUser({
      email,
      password,
    });

    if (error) {
      toast({
        title: "Kontokonvertering misslyckades",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }

    // After user verifies email, the onAuthStateChange listener will trigger
    // and getUserTypeAndProfile() will update is_anonymous/is_claimed flags
    toast({
      title: "Konto skapat framgångsrikt",
      description: "Du kan nu komma åt dina förfrågningar när som helst!",
    });

    return { error: null };
  };

  const signInWithEmail = async (email: string, password: string, loginType: 'customer' | 'supplier' = 'customer') => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      let description = 'Ett oväntat fel uppstod.';
      if (error.message.includes('Invalid login credentials')) {
        description = 'Felaktigt lösenord eller e-postadress. Kontrollera dina uppgifter och försök igen.';
      } else if (error.message.includes('Email not confirmed')) {
        description = 'E-postadressen är inte bekräftad. Kontrollera din inkorg och klicka på bekräftelseslänken.';
      }
      return handleErrorAndToast(error, "Inloggning misslyckades", description);
    }

    if (data.user) {
      // The onAuthStateChange listener will handle the state update and type check
      // This function just needs to return success
      toast({
        title: "Inloggning lyckades",
        description: `Välkommen tillbaka!`,
      });
    }

    return { error: null };
  };

  const signInWithProvider = async (provider: 'google' | 'facebook', rememberMe: boolean = true) => {
    const redirectUrl = `${window.location.origin}/`;

    // Store rememberMe preference for later use after OAuth callback
    if (!rememberMe) {
      sessionStorage.setItem('auth-remember-me', 'false');
    } else {
      sessionStorage.removeItem('auth-remember-me');
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUrl
      }
    });

    if (error) {
      toast({
        title: "Inloggning misslyckades",
        description: error.message,
        variant: "destructive",
      });
    }

    return { error };
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const redirectUrl = `${window.location.origin}/waiting-confirmation?email=${encodeURIComponent(email)}`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: `${firstName} ${lastName}`,
          first_name: firstName,
          last_name: lastName,
        }
      }
    });

    if (error) {
      toast({
        title: "Registrering misslyckades",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }

    toast({
      title: "Registrering lyckades",
      description: "Kolla din e-post för att bekräfta ditt konto.",
    });

    return { error: null };
  };

  const resetPasswordForEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return handleErrorAndToast(error, "Återställning misslyckades", "Kunde inte skicka återställningslänk.");
      }

      toast({
        title: "E-post skickad",
        description: "Vi har skickat en återställningslänk till din e-post.",
      });

      return { error: null };
    } catch (error) {
      return handleErrorAndToast(error, "Fel", "Ett oväntat fel uppstod.");
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      // Check if we actually have a session (verified by the OTP step above)
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        return handleErrorAndToast(
          { message: "Session saknas. Vänligen klicka på länken i mejlet igen." },
          "Uppdatering misslyckades",
          "Din session har gått ut."
        );
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return handleErrorAndToast(error, "Uppdatering misslyckades", "Kunde inte uppdatera lösenordet.");
      }

      toast({
        title: "Lösenord uppdaterat",
        description: "Ditt lösenord har ändrats. Du omdirigeras nu...",
      });

      return { error: null };
    } catch (error) {
      return handleErrorAndToast(error, "Fel", "Ett oväntat fel uppstod.");
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      return handleErrorAndToast(error, "Utloggning misslyckades", "Ett oväntat fel uppstod vid utloggning.");
    }
    // The onAuthStateChange listener handles the state clearing and toast
    return { error: null };
  };

  const refreshSession = async () => {
    // Should this func be deleted?
    try {
      console.log('Refreshing authentication session...');
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
        return false;
      }

      if (data.session) {
        console.log('Session refreshed successfully, updating context');
        setSession(data.session);
        setUser(data.session.user);

        // Force re-validation of user type after session refresh
        const { type, profile } = await getUserTypeAndProfile(data.session.user.id);
        if (type) {
          setUserType(type);
        }
        console.log('Session refreshed and user validated successfully');
        return true;
      }

      console.log('No session returned from refresh');
      return false;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  // Context value object
  const value: AuthContextType = {
    user,
    session,
    loading,
    userType,
    supplierProfile,
    isAnonymous,
    isClaimed,
    signInWithEmail,
    signInWithProvider,
    signUp,
    signOut,
    signInAnonymously,
    checkEmailExists,
    convertAnonymousAccount,
    refreshSession,
    resetPasswordForEmail,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


